import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  OutlinedInput,
  Typography,
} from '@material-ui/core';
import { IoIosArrowDropdownCircle } from 'react-icons/io';
import { BiSortUp, BiSortDown } from 'react-icons/bi';
import { FaSearch } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useStyles } from './styles';
import {
  IEquipmentsData,
  IEquipmentV2,
  IWorkcenter,
} from '../../../types/kpis';
import { sumArrays, transformWeeksToMonths } from '../../../utils/helper';

interface IFilterModal extends DialogProps {
  workcenters: IWorkcenter[];
  equipments: IEquipmentsData;
  onDone?: () => void;
  onClose?: () => void;
  onCancel?: () => void;
  filtered?: (equipments: IEquipmentV2[]) => void;
  year: number;
  onAnnualChange?: (annual: number[]) => void;
}

const FilterModal: React.FC<IFilterModal> = ({
  workcenters,
  equipments,
  onDone,
  onClose,
  filtered,
  year,
  onAnnualChange,
  ...rest
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [selectedWorkcenter, setSelectedWorkcenter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [sort, setSort] = useState('');
  const [filteredEquipments, setFilteredEquipments] = useState<IEquipmentV2[]>(
    equipments.equipments,
  );
  const [filteredAnnual, setFilteredAnnual] = useState<number[]>([]);

  const handleClose = (): void => {
    setSelectedWorkcenter('');
    setSearch('');
    setActiveFilters([]);
    setSort('');
    setFilteredEquipments(equipments.equipments);
    if (onClose) {
      onClose();
    }
  };

  const handleFormChange = (event: any, type: string): void => {
    if (type === 'workcenter') {
      setSelectedWorkcenter(event.target.value);
    }
  };

  const handleChangeFilter = (
    equipmentName: string,
    checked: boolean,
  ): void => {
    if (equipmentName === 'all') {
      if (checked) {
        const allEquipments = equipments.equipments.map(eq => eq.name);
        setActiveFilters(allEquipments);
      } else {
        setActiveFilters([]);
      }
    } else {
      if (checked) {
        setActiveFilters([...activeFilters, equipmentName]);
        return;
      }
      const newFilters = [...activeFilters];
      const eqIndex = newFilters.findIndex(name => name === equipmentName);

      if (eqIndex !== -1) {
        newFilters.splice(eqIndex, 1);
      }

      setActiveFilters(newFilters);
    }
  };

  const handleSort = useCallback(
    (type: string): IEquipmentV2[] => {
      if (type === 'asc') {
        const sorted = equipments.equipments.sort((a, b) => {
          const aValues = a.workcenters.map(wk => wk.values);
          const bValues = b.workcenters.map(wk => wk.values);
          const aSum = sumArrays(...aValues).reduce((acc, sum) => acc + sum, 0);
          const bSum = sumArrays(...bValues).reduce((acc, sum) => acc + sum, 0);
          return aSum - bSum;
        });

        return sorted;
      }
      const sorted = equipments.equipments.sort((a, b) => {
        const aValues = a.workcenters.map(wk => wk.values);
        const bValues = b.workcenters.map(wk => wk.values);
        const aSum = sumArrays(...aValues).reduce((acc, sum) => acc + sum, 0);
        const bSum = sumArrays(...bValues).reduce((acc, sum) => acc + sum, 0);
        return bSum - aSum;
      });

      return sorted;
    },
    [equipments],
  );

  const handleDone = (): void => {
    if (activeFilters.length > 0) {
      const filteredData: IEquipmentV2[] = [];
      for (let i = 0; i < activeFilters.length; i += 1) {
        const equip = equipments.equipments.find(
          eq => eq.name.toLowerCase() === activeFilters[i].toLowerCase(),
        );
        if (equip) {
          filteredData.push(equip);
        }
      }

      if (filteredData.length > 0) {
        const monthlyValues = filteredData.map(ftData => {
          const res = ftData.workcenters.map(wk => wk.values);
          const sum = sumArrays(...res);
          const average = sum.map(val => val / sum.length);
          const transformed = transformWeeksToMonths(average, year);
          return transformed;
        });
        const sumMonthlyValues = sumArrays(...monthlyValues);
        const annual = sumMonthlyValues.map(v => v / monthlyValues.length);
        setFilteredAnnual(annual);
        setFilteredEquipments(filteredData);
        if (filtered) {
          filtered(filteredData);
        }
        if (onAnnualChange) {
          onAnnualChange(annual);
        }
      }
      if (onDone) {
        onDone();
      }
    }
  };

  useEffect(() => {
    if (search !== '') {
      const filteredEquip = equipments.equipments.filter(eq =>
        eq.name.toLowerCase().includes(search.toLowerCase()),
      );
      setFilteredEquipments(filteredEquip);
      return;
    }
    setFilteredEquipments(equipments.equipments);
  }, [search, equipments]);

  useEffect(() => {
    if (selectedWorkcenter !== '') {
      const filteredEquip = equipments.equipments.filter(eq =>
        eq.workcenters.map(
          wk => wk.name.toLowerCase() === selectedWorkcenter.toLowerCase(),
        ),
      );
      setFilteredEquipments(filteredEquip);
      return;
    }
    setFilteredEquipments(equipments.equipments);
  }, [equipments, selectedWorkcenter]);

  useEffect(() => {
    if (sort !== '') {
      const res = handleSort(sort);
      setFilteredEquipments(res);
      return;
    }
    setFilteredEquipments(equipments.equipments);
  }, [sort, equipments, handleSort]);

  return (
    <Dialog
      className={classes.container}
      aria-labelledby={t('screens:dimensioning.editWeekDialogAria')}
      aria-describedby={t('screens:dimensioning.editWeekDialogAriaDescribedBy')}
      {...rest}
    >
      <IconButton
        size="small"
        className={classes.closeButton}
        onClick={() => handleClose()}
      >
        <FiX color="hsla(0, 0%, 20%, 1)" size={16} />
      </IconButton>
      <DialogContent className={classes.content}>
        <div className={classes.inputContainer}>
          <form className={classes.form} noValidate>
            <Typography variant="subtitle1" className={classes.formLabel}>
              {t('components:buttons.filter')}
            </Typography>
            <FormControl className={classes.formControl}>
              <Select
                autoFocus
                value={selectedWorkcenter}
                onChange={event => handleFormChange(event, 'workcenter')}
                variant="outlined"
                IconComponent={props => (
                  <IoIosArrowDropdownCircle
                    size={24}
                    color="#7030A0"
                    style={{ marginLeft: 8 }}
                    {...props}
                  />
                )}
                className={classes.select}
              >
                <MenuItem disabled value={false as any}>
                  {t('screens:dimensioning.workCenter')}
                </MenuItem>
                {workcenters.map(wk => (
                  <MenuItem key={wk.name} value={wk.name}>
                    {wk.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle1" className={classes.formLabel}>
              {t('screens:dimensioning.classify')}
            </Typography>
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
              }}
            >
              <Chip
                label={t('screens:dimensioning.higherStaffQuantity')}
                icon={<BiSortUp />}
                onClick={() => setSort('asc')}
                className={clsx(classes.sortButton, {
                  [classes.sortButtonActive]: sort === 'asc',
                })}
              />
              <Chip
                label={t('screens:dimensioning.lowerStaffQuantity')}
                icon={<BiSortDown />}
                onClick={() => setSort('desc')}
                className={clsx(classes.sortButton, {
                  [classes.sortButtonActive]: sort === 'desc',
                })}
              />
              <OutlinedInput
                id="outlined-search"
                type="search"
                placeholder={t('components:forms.search')}
                value={search}
                onChange={event => setSearch(event.target.value)}
                className={classes.search}
                startAdornment={
                  <FaSearch
                    color="hsla(216, 11%, 65%, 1)"
                    style={{ marginRight: 8 }}
                  />
                }
              />
            </div>

            <div className={classes.selectEquipment}>
              <FormControlLabel
                aria-label="all_equipments"
                onClick={event => event.stopPropagation()}
                onFocus={event => event.stopPropagation()}
                placeholder="Select"
                control={
                  <Checkbox
                    color="primary"
                    size="small"
                    checked={
                      activeFilters.length === equipments.equipments.length
                    }
                    onChange={event =>
                      handleChangeFilter('all', event.target.checked)
                    }
                  />
                }
                label={t('components:forms.selectAll')}
                className={classes.checkbox}
              />
              {filteredEquipments.map(eq => (
                <FormControlLabel
                  key={eq.id}
                  aria-label={`equipment_${eq.name}`}
                  onClick={event => event.stopPropagation()}
                  onFocus={event => event.stopPropagation()}
                  placeholder="Select"
                  control={
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={activeFilters.includes(eq.name)}
                      onChange={event =>
                        handleChangeFilter(eq.name, event.target.checked)
                      }
                    />
                  }
                  label={eq.name}
                  className={classes.checkbox}
                />
              ))}
            </div>
          </form>
        </div>
      </DialogContent>
      <DialogActions className={classes.buttonsContainer}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => handleClose()}
          color="primary"
          className={classes.button}
          disabled={activeFilters.length === 0}
        >
          {t('components:buttons.clear')}
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => handleDone()}
          color="primary"
          className={classes.button}
          disabled={activeFilters.length === 0}
        >
          {t('components:buttons.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterModal;
